/**
 * FADeclarationDialog — one-time onboarding prompt for users who haven't
 * declared their focus area(s) yet.
 *
 * Two modes (#781):
 *   - talent:  pick ANY of FA1-4 as preferred focus areas (multi-select).
 *              Persists `preferred_focus_areas` via PUT /auth/me. Freely
 *              changeable later in the profile, so no admin-lock warning.
 *   - council: pick ONE authoritative FA (single-select, FA1-4 + BO).
 *              Persists `focus_area_code`. This is the first declaration
 *              of admin-managed FA membership — changing it later needs an
 *              admin (#753), so the lock warning stays.
 */

import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Dialog,
  DialogSurface,
  DialogBody,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Spinner,
  MessageBar,
  MessageBarBody,
  makeStyles,
  tokens,
} from '@fluentui/react-components'
import { FOCUS_AREAS } from '../types/focus_area'
import { useUpdateProfile } from '../hooks/useProfile'
import { apiErrorMessage } from '../lib/apiError'
import { bayerColors } from '../theme'

// Talents may prefer any of the four real focus areas. Council members
// additionally declare BO (Business Ops). UN (Unclassified) is never a
// user-pickable onboarding choice.
const TALENT_FA_CODES = ['FA1', 'FA2', 'FA3', 'FA4']
const COUNCIL_FA_CODES = ['FA1', 'FA2', 'FA3', 'FA4', 'BO']

const useStyles = makeStyles({
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
    gap: '10px',
    marginTop: '12px',
    marginBottom: '16px',
  },
  card: {
    padding: '14px 16px',
    borderRadius: '6px',
    border: '2px solid #e6e6e6',
    background: '#fafafa',
    cursor: 'pointer',
    textAlign: 'left',
    transition: 'border-color 120ms, background 120ms',
    ':hover': {
      borderTopColor: '#54749a',
      borderRightColor: '#54749a',
      borderBottomColor: '#54749a',
      borderLeftColor: '#54749a',
      background: '#f0f8ff',
    },
  },
  cardSelected: {
    padding: '14px 16px',
    borderRadius: '6px',
    border: `2px solid ${bayerColors.navy}`,
    background: '#e8f4fd',
    cursor: 'pointer',
    textAlign: 'left',
  },
  cardCode: {
    fontSize: '11px',
    fontWeight: 700,
    color: bayerColors.navyMid,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.5px',
  },
  cardName: {
    fontSize: '14px',
    fontWeight: 600,
    color: tokens.colorNeutralForeground1,
    marginTop: '2px',
  },
  warning: {
    marginTop: '8px',
  },
  subtitle: {
    fontSize: '13px',
    color: tokens.colorNeutralForeground2,
    marginTop: '4px',
    marginBottom: '0',
  },
})

interface FADeclarationDialogProps {
  /** talent = multi-select preferred FAs; council = single authoritative FA. */
  mode?: 'talent' | 'council'
  /** Where to redirect after selection. Defaults per mode. */
  redirectTo?: string
}

export function FADeclarationDialog({ mode = 'council', redirectTo }: FADeclarationDialogProps) {
  const styles = useStyles()
  const navigate = useNavigate()
  const isTalent = mode === 'talent'
  // Talents start with all four selected and opt out by unticking (owner,
  // 2026-10-06). From 2026-10-01 to 10-06 this started empty, because a
  // posting emailed every talent who matched an area and the all-four default
  // made that 209 people. Postings no longer email (#1419), so the default
  // that keeps people reachable is back. Council still picks exactly one.
  const [selected, setSelected] = useState<string[]>(isTalent ? TALENT_FA_CODES : [])
  const [error, setError] = useState<string | null>(null)
  const updateProfile = useUpdateProfile()

  const allowedCodes = isTalent ? TALENT_FA_CODES : COUNCIL_FA_CODES
  const options = FOCUS_AREAS.filter((fa) => allowedCodes.includes(fa.code))

  const toggle = (code: string) => {
    if (isTalent) {
      setSelected((prev) =>
        prev.includes(code) ? prev.filter((c) => c !== code) : [...prev, code],
      )
    } else {
      setSelected([code]) // single-select for council
    }
  }

  const handleConfirm = () => {
    if (selected.length === 0) return
    const patch = isTalent
      ? { preferred_focus_areas: selected }
      : { focus_area_code: selected[0] }
    setError(null)
    updateProfile.mutate(patch, {
      onSuccess: () => {
        window.location.href = redirectTo ?? (isTalent ? '/' : `/focus-areas/${selected[0]}`)
      },
      // This dialog is modal and blocks the app until a focus area is declared.
      // A failure with no message left the user staring at a Confirm button
      // that did nothing, with no way forward and nothing to report.
      onError: (err) => setError(apiErrorMessage(err, 'Could not save your focus area.')),
    })
  }

  return (
    <Dialog open modalType="alert">
      <DialogSurface style={{ maxWidth: 560 }}>
        <DialogBody>
          <DialogTitle>{isTalent ? 'Choose your Focus Areas' : 'Choose your Focus Area'}</DialogTitle>
          <DialogContent>
            <p className={styles.subtitle}>
              {isTalent
                ? 'Untick any you don’t want to hear about. Change it anytime in your profile.'
                : 'Select the Focus Area you lead or coach. This sets your council membership.'}
            </p>

            <div className={styles.grid}>
              {options.map((fa) => (
                <button
                  key={fa.code}
                  type="button"
                  className={selected.includes(fa.code) ? styles.cardSelected : styles.card}
                  onClick={() => toggle(fa.code)}
                  aria-pressed={selected.includes(fa.code)}
                  data-testid={`fa-declare-${fa.code}`}
                >
                  <div className={styles.cardCode}>{fa.code}</div>
                  <div className={styles.cardName}>{fa.name}</div>
                </button>
              ))}
            </div>

            {!isTalent && (
              <MessageBar intent="warning" className={styles.warning}>
                <MessageBarBody>
                  This choice cannot be changed later in the app — contact an admin if you need to
                  switch.
                </MessageBarBody>
              </MessageBar>
            )}

            {error && (
              <MessageBar intent="error" data-testid="fa-declaration-error">
                <MessageBarBody>{error}</MessageBarBody>
              </MessageBar>
            )}
          </DialogContent>
          <DialogActions>
            {/* An escape, because otherwise this is a lock.
                `modalType="alert"` blocks Escape and light-dismiss, Confirm is
                disabled until a card is picked, and the choice is permanent —
                while every "go home" path in the shell (ErrorBoundary,
                RoleGuard, clearTarget) returns to `/`, which re-opens this.
                A council member who genuinely leads no single focus area — a
                real, deliberate state since ticket #18 — had no move except to
                claim edit rights over an area they do not lead. This grants
                nothing: they land on the same cross-FA overview, still with no
                focus area. */}
            {!isTalent && (
              <Button
                appearance="subtle"
                disabled={updateProfile.isPending}
                onClick={() => {
                  // Remember the answer, or `/` re-opens this on the next visit.
                  // Leave at once: a slow or failed save must not trap them here.
                  updateProfile.mutate({ fa_declaration_declined: true })
                  navigate('/focus-areas')
                }}
                data-testid="fa-declare-skip"
              >
                I do not lead one focus area
              </Button>
            )}
            <Button
              appearance="primary"
              disabled={selected.length === 0 || updateProfile.isPending}
              onClick={handleConfirm}
              icon={updateProfile.isPending ? <Spinner size="tiny" /> : undefined}
            >
              {updateProfile.isPending ? 'Saving…' : 'Confirm'}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  )
}
